import React from "react";
import HeroMotionLayer from "./HeroMotionLayer";

function PutraMosqueSilhouette() {
  return (
    <svg
      className="taip-putra-mosque"
      viewBox="0 0 900 310"
      preserveAspectRatio="xMinYMax meet"
      role="presentation"
    >
      <defs>
        <linearGradient id="taipPutraNavy" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#123B8F" stopOpacity="0.34" />
          <stop offset="58%" stopColor="#0B1F4D" stopOpacity="0.76" />
          <stop offset="100%" stopColor="#05102A" stopOpacity="0.94" />
        </linearGradient>
        <linearGradient id="taipPutraGold" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#D6B36A" stopOpacity="0" />
          <stop offset="48%" stopColor="#F3E3B2" stopOpacity="0.42" />
          <stop offset="100%" stopColor="#D6B36A" stopOpacity="0.08" />
        </linearGradient>
        <filter id="taipPutraEdgeGlow" x="-20%" y="-30%" width="140%" height="160%">
          <feGaussianBlur stdDeviation="2.1" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g fill="url(#taipPutraNavy)">
        <path d="M24 278 H770 L744 242 H700 V226 H666 V244 H612 V220 H558 V243 H510 V216 H458 V239 H420 V205 H172 V236 H124 V222 H78 V244 H46 Z" />

        {/* Putra-inspired central onion dome, kept intentionally abstract. */}
        <path d="M196 205 V181 H218 C224 143 253 113 299 91 C345 113 374 143 380 181 H402 V205 Z" />
        <path d="M289 97 L299 73 L309 97 Z" />
        <rect x="214" y="202" width="174" height="56" rx="3" />

        {/* Lower flanking domes integrate the silhouette into the skyline. */}
        <path d="M92 241 V225 H107 C111 202 129 184 157 171 C185 184 203 202 207 225 H222 V241 Z" />
        <path d="M454 239 V223 H469 C473 199 492 181 520 168 C548 181 567 199 571 223 H586 V239 Z" />
        <rect x="104" y="239" width="106" height="35" />
        <rect x="466" y="237" width="108" height="38" />

        {/* Masjid Putra's distinctive tall, slender minaret as a quiet marker. */}
        <path d="M426 250 H478 L470 227 V126 L462 112 L459 82 L452 61 L445 82 L442 112 L434 126 V227 Z" />
        <path d="M437 139 H467 L462 123 H442 Z" />
        <path d="M440 108 H464 L459 93 H445 Z" />
        <path d="M445 76 H459 L452 54 Z" />
      </g>

      <g
        fill="none"
        stroke="url(#taipPutraGold)"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#taipPutraEdgeGlow)"
      >
        <path d="M196 205 V181 H218 C224 143 253 113 299 91 C345 113 374 143 380 181 H402 V205" />
        <path d="M289 97 L299 73 L309 97" />
        <path d="M92 241 V225 H107 C111 202 129 184 157 171 C185 184 203 202 207 225 H222 V241" />
        <path d="M454 239 V223 H469 C473 199 492 181 520 168 C548 181 567 199 571 223 H586 V239" />
        <path d="M426 250 H478 L470 227 V126 L462 112 L459 82 L452 61 L445 82 L442 112 L434 126 V227 Z" />
      </g>
    </svg>
  );
}

/**
 * Decorative TAi-P homepage atmosphere.
 *
 * The responsive artwork is a polished, UI-free edit of the approved visual
 * reference. Live text and controls remain separate so they stay accessible.
 */
export default function MalaysiaBackdrop() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden select-none bg-[#0B1F4D]"
    >
      <picture>
        <source
          media="(max-width: 1023px)"
          srcSet="/taip-premium-islamic-hero-mobile-v2.png"
        />
        <img
          src="/taip-premium-islamic-hero-desktop-v2.png"
          alt=""
          className="taip-premium-backdrop-image absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
      </picture>

      {/* The artwork already contains one crescent moon. This sparse CSS layer
          only extends the quiet night-sky depth across the full viewport. */}
      <div className="taip-night-stars absolute inset-0" aria-hidden="true">
        <span className="taip-night-star taip-night-star-1" />
        <span className="taip-night-star taip-night-star-2" />
      </div>

      {/* Calm the copy area without flattening the gold glow or the globe. */}
      <div className="absolute inset-0 hidden md:block bg-[linear-gradient(90deg,rgba(5,15,40,0.5)_0%,rgba(5,15,40,0.3)_42%,rgba(5,15,40,0.04)_70%,rgba(5,15,40,0.08)_100%)]" />
      <div className="absolute inset-0 md:hidden bg-[linear-gradient(180deg,rgba(5,15,40,0.38)_0%,rgba(5,15,40,0.24)_50%,rgba(5,15,40,0.08)_72%,rgba(5,15,40,0.28)_100%)]" />

      {/* Small edge gradients keep the header and fixed composer legible. */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#071633]/58 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[#071633]/72 via-[#123B8F]/20 to-transparent" />

      <div className="taip-putra-mosque-mask" />
      <PutraMosqueSilhouette />

      <HeroMotionLayer />
    </div>
  );
}

